import {
  SECTIONS,
  NOTIFICATION_TYPES,
  OPERATIONS,
} from "../../../../config/constants";
import PageHead from "../../../layout/PageHead";
import {
  Button,
  Form,
  Input,
  Menu,
  MenuProps,
  Dropdown,
  RadioChangeEvent,
  Drawer,
  Space,
  Radio,
} from "antd";
import "./LoadFileSetup.css";
import {
  EditOutlined,
  CloseOutlined,
  UndoOutlined,
  SwapOutlined,
  FlagOutlined,
  CheckOutlined,
  DownOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import TablesService from "../../../../services/tables/TablesService";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import Mapping from "./components/Mapping";
import MappingTemplateService from "../../../../services/mappingtemplates/MappingTemplatesService";
import TableFieldsService from "../../../../services/tablefields/TableFieldsService";
import { GetTablesRequest } from "../../../../models/tables/GetTablesRequest";
import FieldMappingsService from "../../../../services/fieldmappings/FieldMappingsService";
import { GetFieldMappingsRequest } from "../../../../models/fieldmappings/GetFieldMappingsRequest";
import styles from "./LoadFileSetup.module.css";
import { CreateMappingTemplate } from "../../../../models/mappingtemplates/CreateMappingTemplate";
import { CreateMappingTemplateMapping } from "../../../../models/mappingtemplates/CreateMappingTemplateMapping";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../shared/utils/NotificationsUtil";
import history from "../../../shared/utils/HistoryUtil";
import { useLocation, useSearchParams } from "react-router-dom";
import {ROUTES} from "../../../../config/consts/routes";
import {GetFieldsRequest} from "../../../../models/fields/GetFieldsRequest";

const { Search } = Input;

const routes = [
  {
    path: ROUTES.general.main.route,
    breadcrumbName: SECTIONS.general.name,
  },
  {
    breadcrumbName: SECTIONS.operation.name,
  },
  {
    path: ROUTES.operation.loadFileSetup.route,
    breadcrumbName: ROUTES.operation.loadFileSetup.name,
  },
];

const LoadFileSetup = () => {
  const { t, i18n } = useTranslation("translations");
  const [searchParams, setSearchParams] = useSearchParams();

  const [groupCodes, setGroupCodes] = useState<any>([]);
  const [entities, setEntities] = useState<any>([]);
  const [operations, setOperations] = useState<any>([]);
  const [sourceTableFields, setSourceTableFields] = useState<any>([]);
  const [unmappedFields, setUnmappedFields] = useState<any>([]);
  const [fieldMappings, setFieldMappings] = useState<any>([]);
  const [editableFieldMappings, setEditableFieldMappings] = useState<any>([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState<any>();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [selectedOperation, setSelectedOperation] = useState<any>();
  const [selectedSourceTable, setSelectedSourceTable] = useState<any>();
  const [selectedDestinationTable, setSelectedDestinationTable] = useState<any>();
  const [destinationTables, setDestinationTables] = useState<any>([]);
  const [mappingTemplates, setMappingTemplates] = useState<any>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [editEnabled, setEditEnabled] = useState(true);
  const [triggerUpdate, setTriggerUpdate] = useState(false);
  const [newMappingTemplateName, setNewMappingTemplateName] =
    useState<string>("");
  const [existingMappingTemplate, setSelectedExistingMappingTemplate] =
    useState<number>();
  const [
    existingMappingTemplateSearchInput,
    setExistingMappingTemplateSearchInput,
  ] = useState<string>("");
  const [selectedApplyMappingTemplate, setSelectedApplyMappingTemplate] =
    useState<number>();
  const [applyMappingTemplateSearchInput, setApplyMappingTemplateSearchInput] =
    useState<string>("");

  const [visibleDrawer, setVisibleDrawer] = useState("0");
  const [mappingTemplateType, setMappingTemplateType] = useState(1);

  const [saveMappingTemplateLoading, setSaveMappingTemplateLoading] = useState(false);

  const { state }: any = useLocation();
  const [transferedState, setTransferedState] = useState(null);

  const [isDirty, setIsDirty] = useState<any>(false);

  useEffect(() => {
    if (transferedState && destinationTables) {
      setSelectedDestinationTable(destinationTables.find((x: any) => x.name === transferedState.toTable)?.id);
    } else if (destinationTables?.length === 1) {
      setSelectedDestinationTable(destinationTables[0].id);
    }
  }, [transferedState, destinationTables])

  useEffect(() => {
    if (searchParams.get("toTable")) {
      setTransferedState({toTable: searchParams.get("toTable")})
    }
  }, [searchParams])

  useEffect(() => {
    if (state) {
      setTransferedState(state);
    }
  }, [state]);

  useEffect(() => {
    document.title = ROUTES.operation.loadFileSetup.name;
  }, []);

  useEffect(() => {
    if (!isDirty) return;

    const unblock = history.block((tx:any) => {
      if (window.confirm("Are you sure you want leave the page without saving? Current changes will be lost.")) {
        unblock();
        tx.retry();
      }
    });
  
    return () => {
      unblock();
    };
  }, [isDirty]);

  useEffect(() => {
    const difference = fieldMappings.filter(({ id: id1, fromFieldId: fromFieldId1}) => !editableFieldMappings
        .some(({ id: id2, fromFieldId: fromFieldId2 }) => id2 === id1 && fromFieldId1 === fromFieldId2))
    difference.length
        ? setIsDirty(true)
        : setIsDirty(false);
  }, [editableFieldMappings]);

  useEffect(() => {
    TablesService.getAllEntities().then((response: any) => {
      const data = response.data;
      setEntities(data);
    });
    MappingTemplateService.getAll().then((response: any) => {
      const data = response.data;
      setMappingTemplates(data);
    });
    TablesService.getAllOperations().then((response: any) => {
      const data = response.data;
      setOperations(data);
    });
  }, []);

  useEffect(() => {
    if (operations) {
      setSelectedOperation(OPERATIONS.LOADFILE)
    }
  }, [operations]);

  useEffect(() => {
    setSelectedSourceTable(null);
    setDestinationTables([]);
    setSelectedDestinationTable(null);
    if (selectedEntity) {
      const fromModel: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: 3, // LoadFileValidation
        groupCode: "",
      };
      const toModel: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: 5, // LoadFile
        groupCode: "",
      };

      TablesService.getTables(fromModel).then((response: any) => {
        const data = response.data;
        if (data && data !== undefined && data.length > 0) {
          setSelectedSourceTable(data[0].id);
        }
      });

      TablesService.getTables(toModel).then((response: any) => {
        const data = response.data;
        if (data && data !== undefined && data.length > 0) {
          setDestinationTables(data);
        }
      });
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedSourceTable && selectedDestinationTable) {
      TableFieldsService.getAllFrom(selectedSourceTable).then(
        (response: any) => {
          const data = response.data;
          setSourceTableFields(data);
        }
      );
    } else {
      setSourceTableFields([]);
    }
  }, [selectedSourceTable, selectedDestinationTable, triggerUpdate]);

  useEffect(() => {
    if (sourceTableFields && fieldMappings) {
      setUnmappedFields(
        sourceTableFields.filter(
          (firstEl: any) =>
            fieldMappings.filter(
              (secondEl: any) =>
                secondEl.fromFieldId === firstEl.id ||
                secondEl.fromValue === firstEl.name
            ).length === 0
        )
      );
    } else {
      setUnmappedFields([]);
    }
  }, [sourceTableFields, fieldMappings, triggerUpdate]);

  useEffect(() => {}, [unmappedFields]);

  useEffect(() => {
    if (selectedSourceTable && selectedDestinationTable) {
      setIsTableLoading(true);
      loadFieldMappings();
    } else {
      setFieldMappings([]);
      setIsTableLoading(false);
    }
  }, [selectedSourceTable, selectedDestinationTable, triggerUpdate]);

  const loadFieldMappings = async () => {
    try {
      const model: GetFieldMappingsRequest = {
        groupCode: "",
        fromTableId: selectedSourceTable,
        toTableId: selectedDestinationTable,
      };
      const fieldsModel: GetFieldsRequest = {
        tableId: selectedDestinationTable
      };

      const fieldsResponse = await TablesService.getFields(fieldsModel);
      const fieldMappingsResponse = await FieldMappingsService.getAll(model);
  
      const { data: fieldMappingsData } = fieldMappingsResponse;
      const { data: fieldsData } = fieldsResponse;
  
      const mappedFieldMappings = fieldMappingsData.map((x: any) => {
        const matchingField = fieldsData?.find((y: any) => y.id === x.toFieldId);
  
        return {
          ...x,
          fromValue: x.fromValue === null ? "" : x.fromValue,
          comments: x.comments === null ? "" : x.comments,
          fromField: x.fromField === null ? "" : x.fromField,
          label: matchingField?.label,
          description: matchingField?.description,
          mandatory: matchingField?.isMandatory,
          isSystemField: matchingField?.isSystemField,
          fromTableFields: x.fromFieldId
            ? [
                {
                  fromFieldId: x.fromFieldId,
                  fromField: x.fromField,
                  tableId: x.fromTableId,
                },
              ]
            : [],
        };
      });
  
      setFieldMappings(mappedFieldMappings);
    } catch (error) {
      console.error('Error loading field mappings:', error);
    } finally {
      setIsTableLoading(false);
    }
  };
  

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    showDrawer(e.key);
    if (e.key === "3") {
      automap();
      NotificationsUtil.openNotificationDialog({
        description: t(
          "Page.Operation.LoadfileSetup.Notifications.Mapping.AutomapSuccessDescription",
          {
            lngs: i18n.languages,
            defaultValue: "Automap",
          }
        )!,
        type: NOTIFICATION_TYPES.SUCCESS,
        logError: false,
      });
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setEditEnabled(true);
  };

  const dropdownMenu = (
    <Menu
      className="twoFoldMenu"
      style={{ padding: "10px" }}
      selectable
      onClick={handleMenuClick}
      items={!editEnabled ? [
        {
          label: t("Page.Operation.LoadfileSetup.Labels.ActionSaveAsTemplate", {
            lngs: i18n.languages,
            defaultValue: "Save as template",
          }),
          key: "1",
          icon: <FlagOutlined />,
        },
      ] : [
        {
          label: t("Page.Operation.LoadfileSetupLabels.ActionApplyTemplate", {
            lngs: i18n.languages,
            defaultValue: "Apply template",
          }),
          key: "2",
          icon: <SwapOutlined />,
        },
        {
          label: t("Page.Operation.LoadfileSetup.Labels.ActionAutomap", {
            lngs: i18n.languages,
            defaultValue: "Automap",
          }),
          key: "3",
          icon: <UndoOutlined />,
        },
      ]}
    />
  );

  const showDrawer = (key: string) => {
    setVisibleDrawer(key);
  };

  const onClose = () => {
    setVisibleDrawer("0");
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    setMappingTemplateType(e.target.value);
  };

  const onMappingTemplateNameChange = (value: string) => {
    setNewMappingTemplateName(value);
  };

  const automap = () => {
    let newFieldMappings = editableFieldMappings.map((x: any) => x);
    let newUnmappedFields = unmappedFields.map((x: any) => x);

    for (let i = 0; i < newFieldMappings.length; i += 1) {
      if (
        newFieldMappings[i].fromFieldId === null &&
        (newFieldMappings[i].fromValue === null ||
          newFieldMappings[i].fromValue.length === 0)
      ) {
        const unmapped = newUnmappedFields;
        const fields = newUnmappedFields.filter(
          (el: any) =>
            el?.name?.toLowerCase() ===
            newFieldMappings[i]?.toField?.toLowerCase()
        );
        if (fields && fields.length === 1) {
          newFieldMappings[i].fromFieldId = fields[0].id;
          newFieldMappings[i].fromField = fields[0].name;
          newFieldMappings[i].fromTableFields = fields.splice(0, 1);
        }

        newUnmappedFields = unmapped.filter(
          (el: any) => el.id !== newFieldMappings[i].fromFieldId
        );

        setEditableFieldMappings(newFieldMappings);
        setUnmappedFields(newUnmappedFields);
      }
    }
  };

  const saveMappingTemplate = () => {
    setSaveMappingTemplateLoading(true);
    // this.loading = true;
    const mappedRows = fieldMappings.filter(
      (el: any) => el.fromFieldId != null || el.fromValue.length
    );

    if (mappingTemplateType === 1) {
      // new template
      const model: CreateMappingTemplate = {
        tableID: parseInt(selectedDestinationTable, 10),
        name: newMappingTemplateName,
      };
      MappingTemplateService.create(model).then(
        (templateCreateResponse: any) => {
          MappingTemplateService.getAll().then((templateGetResponse: any) => {
            const data = templateGetResponse.data;
            setMappingTemplates(data);

            const rowsModel: CreateMappingTemplateMapping[] = fieldMappings.map(
              (element: any) => ({
                fromValue:
                  element.fromFieldId != null
                    ? element.fromField
                    : element.fromValue,
                toTableId: element.toTableId,
                toFieldId: element.toFieldId,
                link: element.link,
                comments: element.comments,
                templateId: templateCreateResponse.data,
              })
            );

            MappingTemplateService.createMappingsBulk(rowsModel)
              .then((bulkCreateResponse) => {
                setVisibleDrawer("0");
                setSaveMappingTemplateLoading(false);
                NotificationsUtil.openNotificationDialog({
                  description: t(
                    "Page.Operation.LoadfileSetup.Notifications.MappingTemplate.CreateSuccessDescription",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Mapping Template successfully created.",
                    }
                  )!,
                  type: NOTIFICATION_TYPES.SUCCESS,
                  logError: false,
                });
              })
              .catch((error: any) => {
                NotificationsUtil.openNotificationDialog({
                  description: error.response.data,
                  type: NOTIFICATION_TYPES.ERROR,
                  logError: true,
                });
                setVisibleDrawer("0");
                setSaveMappingTemplateLoading(false);
              });
          });
        }
      );
    } else {
      // existing template
      if (existingMappingTemplate) {
        const rowsModel = mappedRows.map((element: any) => ({
          fromValue:
            element.fromFieldId != null ? element.fromField : element.fromValue,
          toTableId: element.toTableId,
          toFieldId: element.toFieldId,
          link: element.link,
          comments: element.comments,
          templateId: existingMappingTemplate,
        }));

        MappingTemplateService.removeAllMappings(existingMappingTemplate).then(
          (response: any) => {
            MappingTemplateService.createMappingsBulk(rowsModel)
              .then((bulkCreateResponse) => {
                NotificationsUtil.openNotificationDialog({
                  description: t(
                    "Page.Operation.LoadfileSetup.Notifications.MappingTemplate.UpdateSuccessDescription",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Mapping Template successfully updated.",
                    }
                  )!,
                  type: NOTIFICATION_TYPES.SUCCESS,
                  logError: false,
                });
                setVisibleDrawer("0");
                setSaveMappingTemplateLoading(false);
              })
              .catch((error: any) => {
                NotificationsUtil.openNotificationDialog({
                  description: error.response.data,
                  type: NOTIFICATION_TYPES.ERROR,
                  logError: true,
                });
                setVisibleDrawer("0");
                setSaveMappingTemplateLoading(false);
              });
          }
        );
      } else {
        setSaveMappingTemplateLoading(false);
      }
    }
  };

  const applyTemplate = () => {
    // this.loading = true;
    if (!selectedApplyMappingTemplate) {
      return;
    }

    MappingTemplateService.getAllMappings(selectedApplyMappingTemplate).then(
      (response) => {
        const newEditableFieldMappings = editableFieldMappings.map(
          (x: any) => x
        );

        let unmapped = unmappedFields.map((x: any) => x);
        for (let i = 0; i < response.data.length; i += 1) {
          let rowIndex = -1;
          const fields = newEditableFieldMappings.filter(
            (el: any) => el.toFieldId === response.data[i].toFieldId
          );
          if (fields && fields.length === 1) {
            rowIndex = newEditableFieldMappings.indexOf(fields[0]);
          }
          if (rowIndex !== -1) {
            newEditableFieldMappings[rowIndex].fromValue =
              response.data[i].fromValue;
            const fields = unmapped.filter(
              (el: any) =>
                el.name.toLowerCase() ===
                newEditableFieldMappings[rowIndex].fromValue.toLowerCase()
            );
            if (fields.length === 1) {
              newEditableFieldMappings[rowIndex].fromFieldId = fields[0].id;
              newEditableFieldMappings[rowIndex].fromField = fields[0].name;
              newEditableFieldMappings[rowIndex].fromTableFields =
                fields.splice(0, 1);
              newEditableFieldMappings[rowIndex].fromValue = "";
            }
            unmapped = unmapped.filter(
              (el: any) =>
                el.id !== newEditableFieldMappings[rowIndex].fromFieldId
            );
            setUnmappedFields([...unmapped]);
          }
        }
        setEditableFieldMappings(newEditableFieldMappings);
      }
    );
  };

  const handleExistingMappingTemplateChange: MenuProps["onClick"] = (e) => {
    setSelectedExistingMappingTemplate(parseInt(e.key, 10));
  };

  const handleApplyMappingTemplateChange: MenuProps["onClick"] = (e) => {
    // setIsTableLoading(true);
    setSelectedApplyMappingTemplate(parseInt(e.key, 10));
  };

  const onExistingMappingTemplateSearch = (value: string) => {
    setExistingMappingTemplateSearchInput(value);
  };

  const onApplyMappingTemplateSearch = (value: string) => {
    setApplyMappingTemplateSearchInput(value);
  };

  const actionButton = (
    <Dropdown.Button
      disabled={!selectedEntity || !selectedDestinationTable}
      type={
        !selectedEntity || !selectedDestinationTable ? "default" : "primary"
      }
      onClick={handleButtonClick}
      overlay={dropdownMenu}
      className={editEnabled ? styles.buttonActive : ""}
      style={{ borderColor: "var(--white)" }}
      placement="bottomRight"
      // arrow={DropdownArrowOptions.}
      trigger={["click"]}
      icon={<DownOutlined />}
    >
      {/*<EditOutlined />*/}
      {/*{t("Page.Operation.LoadfileSetup.Labels.ActionEdit", {*/}
      {/*  lngs: i18n.languages,*/}
      {/*  defaultValue: "EDIT",*/}
      {/*})}*/}
    </Dropdown.Button>
  );

  return (
    <div>
      <PageHead
        title={t("Page.Operation.LoadfileSetup.Headers.LoadfileSetup", {
          lngs: i18n.languages,
          defaultValue: "Loadfile Setup",
        })}
        icon={<FileDoneOutlined />}
        routes={routes}
        groupCodes={groupCodes}
        entities={entities}
        operations={operations}
        selectedGroupCode={selectedGroupCode}
        selectedEntity={selectedEntity}
        selectedOperation={selectedOperation}
        setSelectedGroupCode={setSelectedGroupCode}
        setSelectedEntity={setSelectedEntity}
        setSelectedOperation={setSelectedOperation}
        setSelectGroupCodeDisabled={true}
        setSelectEntityDisabled={false}
        setSelectOperationDisabled={true}
        mainAction={actionButton}
        otherActions={[]}
        setActionsDisabled={false}
      />
      <div className="loadfile-container">
        <DndProvider backend={HTML5Backend}>
          <Mapping
            selectedEntity={selectedEntity}
            selectedGroupCode={selectedGroupCode}
            isTableLoading={isTableLoading}
            setIsTableLoading={setIsTableLoading}
            selectedSourceTable={selectedSourceTable}
            selectedDestinationTable={selectedDestinationTable}
            unmappedFields={unmappedFields}
            setUnmappedFields={setUnmappedFields}
            sourceTableFields={sourceTableFields}
            fieldMappings={fieldMappings}
            editableFieldMappings={editableFieldMappings}
            setEditableFieldMappings={setEditableFieldMappings}
            setFieldMappings={setFieldMappings}
            editEnabled={editEnabled}
            setEditEnabled={setEditEnabled}
            triggerUpdate={triggerUpdate}
            setTriggerUpdate={setTriggerUpdate}
            destinationTables={destinationTables}
            setSelectedDestinationTable={setSelectedDestinationTable}
          />
        </DndProvider>
      </div>
      <Drawer
        width={"400px"}
        title={t("Page.Operation.LoadfileSetup.Labels.ActionSaveAsTemplate", {
          lngs: i18n.languages,
          defaultValue: "Save as template",
        })}
        placement="right"
        closable={false}
        visible={visibleDrawer === "1"}
        extra={
          <Space>
            <Button
              loading={saveMappingTemplateLoading}
              onClick={saveMappingTemplate}
              icon={<CheckOutlined />}
              size="small"
            >
              {t("General.Labels.Save", {
                lngs: i18n.languages,
                defaultValue: "Save",
              })}
            </Button>
            <Button
              type="ghost"
              loading={saveMappingTemplateLoading}
              onClick={onClose}
              icon={<CloseOutlined />}
              size="small"
            />
          </Space>
        }
      >
        <Radio.Group onChange={onRadioChange} value={mappingTemplateType}>
          <Radio value={1}>
            {t("Page.Operation.LoadfileSetup.Labels.NewTemplate", {
              lngs: i18n.languages,
              defaultValue: "New template",
            })}
          </Radio>
          <Radio value={2}>
            {" "}
            {t("Page.Operation.LoadfileSetup.Labels.ExistingTemplate", {
              lngs: i18n.languages,
              defaultValue: "Existing template",
            })}
          </Radio>
        </Radio.Group>

        {mappingTemplateType === 1 && (
          <Form name="drawer form" layout="vertical">
            <Form.Item
              label={t(
                "Page.Operation.LoadfileSetup.Labels.MappingTemplateName",
                {
                  lngs: i18n.languages,
                  defaultValue: "Mapping template name:",
                }
              )}
              tooltip={t(
                "Page.Operation.LoadfileSetupTooltips.MappingTemplateName",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
              name="templateName"
              rules={[{ required: true, message: "Please input name" }]}
            >
              <Input
                placeholder={
                  t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })!
                }
                value={newMappingTemplateName}
                maxLength={250}
                onChange={(e) => onMappingTemplateNameChange(e.target.value)}
              />
            </Form.Item>
          </Form>
        )}

        {mappingTemplateType === 2 && (
          <>
            <Form layout="vertical">
              <Form.Item
                label={t("Page.Operation.LoadfileSetup.Labels.ChooseTemplate", {
                  lngs: i18n.languages,
                  defaultValue: "Choose template:",
                })}
                tooltip={t(
                  "Page.Operation.LoadfileSetup.Tooltips.ChooseTemplate",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
                style={{ marginBottom: "0" }}
              >
                <Search
                  placeholder={
                    t(
                      "Page.Operation.LoadfileSetup.Placeholders.SearchTemplate",
                      {
                        lngs: i18n.languages,
                        defaultValue: "Search template",
                      }
                    )!
                  }
                  value={existingMappingTemplateSearchInput}
                  onChange={(e) =>
                    onExistingMappingTemplateSearch(e.target.value)
                  }
                />
              </Form.Item>
            </Form>
            <Menu
              className="templateMenu"
              onClick={handleExistingMappingTemplateChange}
              items={mappingTemplates
                .filter((x: any) =>
                  x.name.toLowerCase().includes(existingMappingTemplateSearchInput.toLowerCase())
                )
                .map((item: any) => {
                  return {
                    key: item.id,
                    label: item.name,
                  };
                })}
            />
          </>
        )}
      </Drawer>
      <Drawer
        width={"400px"}
        title={
          t("Page.Operation.LoadfileSetupLabels.ActionApplyTemplate", {
            lngs: i18n.languages,
            defaultValue: "Apply template",
          })!
        }
        placement="right"
        closable={false}
        visible={visibleDrawer === "2"}
        extra={
          <Space>
            <Button
              onClick={applyTemplate}
              icon={<CheckOutlined />}
              size="small"
            >
              {t("General.Labels.Apply", {
                lngs: i18n.languages,
                defaultValue: "Apply",
              })}
            </Button>
            <Button
              type="ghost"
              onClick={onClose}
              icon={<CloseOutlined />}
              size="small"
            />
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Choose template:" style={{ marginBottom: "0" }}>
            <Search
              placeholder={
                t("Page.Operation.LoadfileSetup.Placeholders.SearchTemplate", {
                  lngs: i18n.languages,
                  defaultValue: "Search template",
                })!
              }
              value={applyMappingTemplateSearchInput}
              onChange={(e) => onApplyMappingTemplateSearch(e.target.value)}
            />
          </Form.Item>
        </Form>
        <Menu
          className="templateMenu"
          onClick={handleApplyMappingTemplateChange}
          items={mappingTemplates
            .filter((x: any) =>
              x.name.toLowerCase().includes(applyMappingTemplateSearchInput.toLowerCase())
            )
            .map((item: any) => {
              return {
                key: item.id,
                label: item.name,
              };
            })}
        />
      </Drawer>
    </div>
  );
};

export default LoadFileSetup;
